import {
  defaultTheme,
  parseThemePreferences,
  themeStorageKey,
  type ThemePreferences,
} from './theme-options'

export interface ThemeSnapshot extends ThemePreferences {
  resolvedTheme: 'light' | 'dark'
}
const serverSnapshot: ThemeSnapshot = { ...defaultTheme, resolvedTheme: 'dark' }
let snapshot = serverSnapshot
let initialized = false
let media: MediaQueryList | undefined
const listeners = new Set<() => void>()
const themeTransitionClass = 'theme-transition-active'
const themeTransitionContractClass = 'theme-transition-contract'
let activeThemeTransition: ViewTransition | undefined
let activeThemeAnimation: Animation | undefined
let themeTransitionSequence = 0

interface ThemeTransitionOptions {
  transitionOrigin?: Element
}

interface ThemeSnapshotAnimationOptions extends KeyframeAnimationOptions {
  pseudoElement: string
}

interface ApplyThemeOptions {
  deferBrowserThemeColor?: boolean
  deferListeners?: boolean
}

function readStoredPreferences(): ThemePreferences {
  try {
    return parseThemePreferences(
      JSON.parse(window.localStorage.getItem(themeStorageKey) ?? 'null'),
    )
  } catch {
    return { ...defaultTheme }
  }
}

function resolveTheme(preferences: ThemePreferences): 'light' | 'dark' {
  return preferences.mode === 'system'
    ? media?.matches
      ? 'dark'
      : 'light'
    : preferences.mode
}

function updateBrowserThemeColor(root: HTMLElement) {
  const browserColor = getComputedStyle(root)
    .getPropertyValue('--bg-base')
    .trim()
  if (browserColor)
    document
      .querySelector('meta[name=theme-color]')
      ?.setAttribute('content', browserColor)
}

function emitThemeChange() {
  listeners.forEach((listener) => listener())
}

function apply(preferences: ThemePreferences, options: ApplyThemeOptions = {}) {
  const resolvedTheme = resolveTheme(preferences)
  if (typeof document !== 'undefined') {
    const root = document.documentElement
    root.dataset.theme = resolvedTheme
    root.dataset.palette = preferences.palette
    root.dataset.accent = preferences.accent
    root.style.colorScheme = resolvedTheme
    if (!options.deferBrowserThemeColor) updateBrowserThemeColor(root)
  }
  if (
    snapshot.mode === preferences.mode &&
    snapshot.palette === preferences.palette &&
    snapshot.accent === preferences.accent &&
    snapshot.resolvedTheme === resolvedTheme
  )
    return false
  snapshot = { ...preferences, resolvedTheme }
  if (!options.deferListeners) emitThemeChange()
  return true
}

export function initializeTheme() {
  if (initialized || typeof window === 'undefined') return
  initialized = true
  media = window.matchMedia?.('(prefers-color-scheme: dark)')
  apply(readStoredPreferences())
  media?.addEventListener('change', () => {
    if (snapshot.mode === 'system') apply(snapshot)
  })
  window.addEventListener('storage', (event) => {
    if (event.key !== themeStorageKey && event.key !== null) return
    try {
      apply(parseThemePreferences(JSON.parse(event.newValue ?? 'null')))
    } catch {
      apply(defaultTheme)
    }
  })
}

function persistPreferences(next: ThemePreferences) {
  try {
    window.localStorage.setItem(themeStorageKey, JSON.stringify(next))
  } catch {
    /* Preferences still work when storage is blocked. */
  }
}

function persistAndApply(next: ThemePreferences) {
  persistPreferences(next)
  apply(next)
}

function clearThemeTransition(root: HTMLElement) {
  root.classList.remove(themeTransitionClass, themeTransitionContractClass)
}

function animateThemeSnapshot(
  root: HTMLElement,
  contractsToOrigin: boolean,
  x: number,
  y: number,
  radius: number,
) {
  if (typeof root.animate !== 'function') return undefined

  const collapsed = `circle(0px at ${x}px ${y}px)`
  const expanded = `circle(${radius}px at ${x}px ${y}px)`
  const options: ThemeSnapshotAnimationOptions = {
    duration: 600,
    easing: 'cubic-bezier(0.2, 0.4, 0.8, 0.9)',
    fill: 'both',
    pseudoElement: contractsToOrigin
      ? '::view-transition-old(root)'
      : '::view-transition-new(root)',
  }

  try {
    return root.animate(
      {
        clipPath: contractsToOrigin
          ? [expanded, collapsed]
          : [collapsed, expanded],
      },
      options,
    )
  } catch {
    /* The theme still switches if pseudo-element animation is unavailable. */
    return undefined
  }
}

export function setThemePreferences(
  patch: Partial<ThemePreferences>,
  options: ThemeTransitionOptions = {},
) {
  initializeTheme()
  const next = parseThemePreferences({ ...snapshot, ...patch })
  const origin = options.transitionOrigin
  const startViewTransition = document.startViewTransition?.bind(document)
  const reduceMotion = window.matchMedia?.(
    '(prefers-reduced-motion: reduce)',
  ).matches

  if (!origin || !startViewTransition || reduceMotion) {
    persistAndApply(next)
    return
  }

  const bounds = origin.getBoundingClientRect()
  const x = bounds.left + bounds.width / 2
  const y = bounds.top + bounds.height / 2
  const viewportWidth =
    document.documentElement.clientWidth || window.innerWidth
  const viewportHeight =
    document.documentElement.clientHeight || window.innerHeight
  const radius = Math.hypot(
    Math.max(x, viewportWidth - x),
    Math.max(y, viewportHeight - y),
  )
  const root = document.documentElement
  const sequence = ++themeTransitionSequence
  const contractsToOrigin =
    snapshot.resolvedTheme === 'light' && resolveTheme(next) === 'dark'

  activeThemeTransition?.skipTransition?.()
  activeThemeAnimation?.cancel()
  activeThemeAnimation = undefined
  root.classList.add(themeTransitionClass)
  root.classList.toggle(themeTransitionContractClass, contractsToOrigin)

  try {
    let hasDeferredListeners = false
    let deferredWorkComplete = false
    let transitionAnimation: Animation | undefined
    const completeDeferredWork = () => {
      if (deferredWorkComplete || sequence !== themeTransitionSequence) return
      deferredWorkComplete = true
      persistPreferences(next)
      if (hasDeferredListeners) emitThemeChange()
    }
    const transition = startViewTransition(() => {
      hasDeferredListeners = apply(next, {
        deferBrowserThemeColor: true,
        deferListeners: true,
      })
    })
    activeThemeTransition = transition
    void transition.ready.then(
      () => {
        if (sequence !== themeTransitionSequence) return
        transitionAnimation = animateThemeSnapshot(
          root,
          contractsToOrigin,
          x,
          y,
          radius,
        )
        activeThemeAnimation = transitionAnimation
      },
      () => undefined,
    )
    void transition.finished
      .catch(() => undefined)
      .finally(() => {
        transitionAnimation?.cancel()
        if (sequence !== themeTransitionSequence) return
        completeDeferredWork()
        activeThemeTransition = undefined
        activeThemeAnimation = undefined
        updateBrowserThemeColor(root)
        clearThemeTransition(root)
      })
  } catch {
    activeThemeTransition = undefined
    clearThemeTransition(root)
    persistAndApply(next)
  }
}
export function subscribeToTheme(listener: () => void) {
  initializeTheme()
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
export const getThemeSnapshot = () => snapshot
export const getServerThemeSnapshot = () => serverSnapshot
